import "./LoginModal.css";
import { useRef, useState } from "react";
import SubmitButton from "../../SubmitButton/SubmitButton";
import InputField from "../../CredentialsInput/InputField";
import Logo from "../../../images/logo-symbol.svg";
import xIcon from "../../../../src/icons/X.svg";
import { useCustomer } from "../../../customHooks/useCustomer";
import * as translator from "../../../utils/translator";
import * as http from "../../../utils/http";
import useForm from "../../../customHooks/useForm";
import SocialLoginButtons from "../../LoginStrategies/SocialLoginButtons";
import { useCartContext } from "../../../customHooks/useCart";
import { clientConfig } from "../../../config";
import ReCAPTCHA from "react-google-recaptcha";

export default function LoginModal({
  closeModal,
  setStickyNotificationDetails,
  navigationCallback,
  showForgotPasswordOption = true,
  changeShowForgotPassword,
  closeable = true,
  language,
  country,
  message = "",
  customerVerificationType = '',
  redirectPath = null,
  cameFromPath = null,
  registerHref = null,
  idPrefix = "",
}) {
  const { updateCart } = useCartContext();
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const { saveCustomer } = useCustomer();
  const reRef = useRef(null);
  const loginFormAttributes = useForm({ email: "", loginPassword: "", country }, language, undefined, { looseEmail: true });

  const onSubmit = async (e) => {
    e.preventDefault();

    setSubmitButtonLoading(true);

    if (loginFormAttributes.validateForm()) {

      let captchaToken

      if (clientConfig.IS_RECAPTCHA_ENABLED) {
        captchaToken = await reRef.current?.executeAsync();
        reRef.current?.reset();
      }

      const requestBody = {...loginFormAttributes.form, captchaToken };
      await http
        .postToServer(`auth/local/login`, requestBody, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.ENCRYPTED_BODY])
        .then((response) => {
          if (response.data.status == "ok") {
            saveCustomer({
              customerID: response.data.customerID,
              uuid: response.data.uuid,
              token: response.data.token,
              email: loginFormAttributes.form.email,
              optForSendables: response.data.optForSendables,
              fullName: response.data.customerFullName,
              loginSource: "loginForm",
              provider: response.data.provider || 'local',
              gender: response.data?.gender,
              lastPaidCategoryMention: response.data?.lastPaidCategoryMention,
            });
            const exceededCategoryLimit = response.data.exceededCategoryLimit;
            const cameFromMention = response.data.cameFromMention;
            const shouldIDVerify = response.data.shouldIDVerify;
            closeModal();
            const cartHasProducts = response.data.cartHasProducts;

            updateCart({should_id_verify: shouldIDVerify})

            navigationCallback(exceededCategoryLimit, cameFromMention, shouldIDVerify, cartHasProducts);

          } else {
            setStickyNotificationDetails((currentDetails) => ({
              show: true,
              title: translator.translate(
                language,
                "default",
                "StickyNotification/login_failed"
              ),
              content: translator.translate(
                language,
                "default",
                "StickyNotification/login_failed"
              ),
              level: "error",
              showInfoIcon: true,
              refresh: !currentDetails.refresh
            }));
            closeModal();
          }
        })
        .catch((error) => {

          let stickyContent = translator.translate(
            language,
            "default",
            "StickyNotification/danger_title"
          )

          if (error.response?.data.status === 'user_blocked') {
            stickyContent = translator.translate(language, "default", `StickyNotification/user_blocked`)
          }

          setStickyNotificationDetails((currentDetails) => ({
            show: true,
            title: stickyContent,
            content: "Server Failed",
            level: "error",
            showInfoIcon: true,
            refresh: !currentDetails.refresh
          }));

          window.clientLogger.error("Loggin failed", error);
        });

    }

    setSubmitButtonLoading(false);
  };

  return (
    <>
      <div
        className="loginModal-background"
        onClick={(event) => {
          closeModal();
        }}
      >
        {/*event.stopPropagation => so it won't close the modal when clicking on form fields */}
        <form
          className="loginModal"
          onClick={(event) => event.stopPropagation()}
        >
          {closeable && (
            <img
              id={idPrefix ? `${idPrefix}_close` : undefined}
              onClick={() => closeModal()}
              className="loginModal-x"
              src={xIcon}
              alt="x"
            />
          )}
          <img className="loginModal-logo" src={Logo} alt="Logo" />
          <p className="loginModal-title">
            {translator.translate(
              language,
              "default",
              "LoginQuestion/login_title"
            )}
          </p>
          {message.length > 0 && <div className="loginModal-message">
            <p>
              {message}
            </p>
          </div>}

          {/* Buttons for social login */}
          <SocialLoginButtons
            googleButtonId={idPrefix ? `${idPrefix}_google` : undefined}
            redirectPath={redirectPath ? redirectPath : `${country}/login`}
            cameFromPath={cameFromPath ? cameFromPath : `${country}/login`}
            customerVerificationType={customerVerificationType}
            language={language}
            country={country}
            onCompleteSignup={navigationCallback}
            onFailureSignup={() => {     // Show sticky notification error
              setStickyNotificationDetails({
                show: true,
                title: translator.translate(language, "default", "StickyNotification/danger_title"),
                content: translator.translate(language, "default", "StickyNotification/oauth_failed"),
                level: "error", 
                showInfoIcon: true,
                refresh: false
              });
             }}
          />

          {/* Divider */}
          <div className="login-modal-form-container-or">
            <div className="login-modal-form-container-or-line" />
            <span className="login-modal-form-container-or-text">OR</span>
            <div className="login-modal-form-container-or-line" />
          </div>

          <InputField
            id="email"
            value={loginFormAttributes.form["email"]}
            errorMsg={loginFormAttributes.errorMessagePerField["email"]}
            onChange={(value) => {
              loginFormAttributes.updateForm("email", value);
            }}
            type="email"
            placeholder={"E-Mail"}
            language={language}
            mandatory
            onBlur={() => loginFormAttributes.onFieldBlur("email")}
            touched={loginFormAttributes.touched.email}
          />
          <InputField
            id="loginPassword"
            value={loginFormAttributes.form["loginPassword"]}
            errorMsg={loginFormAttributes.errorMessagePerField["loginPassword"]}
            onChange={(value) => {
              loginFormAttributes.updateForm("loginPassword", value);
            }}
            type="password"
            placeholder={translator.translate(
              language,
              "default",
              "LoginQuestion/password"
            )}
            language={language}
            mandatory
            onBlur={() => loginFormAttributes.onFieldBlur("loginPassword")}
            touched={loginFormAttributes.touched.loginPassword}
          />

          {clientConfig.IS_RECAPTCHA_ENABLED &&
          <ReCAPTCHA
            sitekey={clientConfig.RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={reRef}
          />
          }

          <SubmitButton
            id={"submit-button"}

            loading={submitButtonLoading}
            onSubmit={onSubmit}
            width={"100%"}
            language={language}
            placeholder={translator.translate(
              language,
              "default",
              "LoginQuestion/login_and_continue"
            )}
          />

          {showForgotPasswordOption && (
            <p
              id={idPrefix ? `${idPrefix}_forgot_password` : undefined}
              onClick={() => {
                closeModal();
                changeShowForgotPassword && changeShowForgotPassword(true);
              }}
              className="loginquestion-forgot-password"
            >
              {translator.translate(
                language,
                "default",
                "LoginQuestion/forgot_password"
              )}
            </p>
          )}

          {registerHref && (
            <p className="loginModal-register">
              {translator.translate(language, "authentication", "LoginPage/new_to_doktorabc")}{" "}
              <a id={idPrefix ? `${idPrefix}_register_link` : undefined} href={registerHref} className="loginModal-register-link">
                {translator.translate(language, "authentication", "LoginPage/register_now")}
              </a>
            </p>
          )}
          <input type="submit" hidden />
        </form>
      </div>
    </>
  );
}
